(ns practitest.frontend.gherkin-textarea
  (:require
   ["codemirror" :as cm]
   ["/codemirror/gherkin-light-parser"]
   [clojure.string :as str]
   [practitest.frontend.common.utils :as utils]
   [practitest.frontend.components.icons :as icons]
   [practitest.frontend.init :as init]
   [practitest.frontend.subs :as subs]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [reagent.dom :as rd]))

(rf/reg-sub
  ::gherkin-state
  (fn [db _]
    (:gherkin-state db)))

(rf/reg-sub
  ::scenario-steps
  (fn [_]
    (rf/subscribe [::gherkin-state]))
  (fn [state _]
    (:scenario-steps state)))

(rf/reg-sub
  ::test-name-element-id
  :<- [::subs/options]
  (fn [{:keys [test-name-element-id]} _]
    test-name-element-id))

(rf/reg-sub
  ::gherkin-mode
  (fn [_]
    (rf/subscribe [::gherkin-state]))
  (fn [state _]
    (or (:edit-mode state) false)))

(rf/reg-sub
  ::disabled-edit
  :<- [::subs/options]
  (fn [{:keys [edit-enabled]} _]
    (not edit-enabled)))

(rf/reg-event-db
  ::set-variables
  (fn [db [_ variables]]
    (assoc-in db [:gherkin-state :scenario-variables] variables)))

(rf/reg-event-db
  ::set-steps
  (fn [db [_ steps]]
    (assoc-in db [:gherkin-state :scenario-steps] steps)))

(rf/reg-event-db
  ::toggle-mode
  (fn [db _]
    (let [current-mode (:edit-mode (:gherkin-state db))]
      (assoc-in db [:gherkin-state :edit-mode] (not current-mode)))))

(def default-codemirror-opts
  {:theme       "default"
   :mode        "gherkin-light"})

(rf/reg-fx
  ::edit-test-name
  (fn [[old-value new-value options]]
    (let [first-old       (first (str/split old-value "\n"))
          first-new       (first (str/split new-value "\n"))
          old-name        (str/replace first-old #"Scenario: |Scenario Outline: " "")
          new-name        (str/replace first-new #"Scenario: |Scenario Outline: " "")
          entity-name     (js/document.getElementById (:entity-name-element-id options))
          edit-name       (js/document.getElementById (:edit-name-element-id options))
          with-pre        (or (str/starts-with? first-new "Scenario: ")
                              (str/starts-with? first-new "Scenario Outline: "))]
      (when (and with-pre (not (= old-name new-name)))
        (when entity-name (set! (.-innerHTML entity-name) new-name))
        (when edit-name (set! (.-value edit-name) new-name))))))

(rf/reg-event-fx
  ::trigger-edit-test-name
  (fn [{:keys [db]} [_ old-value new-value]]
    {::edit-test-name [old-value new-value (:options db)]}))

(def cm-container (atom nil))

(defn codemirror [{:keys [value codemirror-opts test-name-element-id]}]
  (let []
    (r/create-class
      {:display-name "practitest.frontend.gherkin.codemirror"
       :component-did-mount
       (fn [this]
         (reset! cm-container (.fromTextArea cm
                                             (rd/dom-node this)
                                             (clj->js (merge default-codemirror-opts
                                                             codemirror-opts))))

         (when-let [f (:on-mount (r/props this))]
           (f @cm-container))

         ;; Get the fn for each event from current props
         (doto @cm-container
           (.setValue (or (:value (r/props this))
                          (:default-value (r/props this))
                          ""))
           (.on "change" (fn [cm change]
                           ((:on-change (r/props this) identity) cm change)))
           (.on "beforeChange" (fn [cm change]
                                 ((:on-before-change (r/props this) identity) cm change)))
           (.on "cursorActivity" (fn [cm]
                                   ((:on-cursor-activity (r/props this) identity) cm)))))
       :component-did-update
       (fn [this [_ prev-props]]
         (let [test-name-element-id  (:test-name-element-id (r/props this))
               old-value             (:value prev-props)
               new-value             (:value (r/props this))
               textarea              (js/document.getElementById test-name-element-id)]
           (rf/dispatch [::trigger-edit-test-name old-value new-value])

           (when-not (= old-value new-value)
             (when textarea (set! (.-value textarea) new-value))))

         (when-let [new-value (:value (r/props this))]
           ;; Not checked against (:value prev-props) as that causes problems with parinfer
           ;; not sure if any benefit in using that when not using parinfer?
           (when (not= (.getValue @cm-container) new-value)
             (.setValue @cm-container new-value))))
       :reagent-render
       (fn [{:keys [value codemirror-opts]}]
         [:textarea])})))

(defn ^:export gherkin-change-name [new-name]
  (let [value            @(rf/subscribe [::scenario-steps])
        lines            (str/split value "\n")
        first-line       (first lines)
        rest-of-lines    (str/join "\n" (drop 1 lines))
        with-pre         (or (str/starts-with? first-line "Scenario: ")
                             (str/starts-with? first-line "Scenario Outline: "))
        pre-name         (when with-pre (str (first (str/split first-line ": ")) ": "))
        new-first-line   (when with-pre (str pre-name new-name))]
    (when with-pre (rf/dispatch [::set-steps (str new-first-line "\n" rest-of-lines)]))))

(defn gherkin-editor-form3
  [{:keys [value edit? on-change on-change2]}]
  (let [set-name-element-value
        (fn [new-value]
          (when-let [name-element (.getElementById js/document "name")]
            (let [scenario-name (->> (re-find #"(?i)Scenario Outline:\s*(.*)|Scenario:\s*(.*)" new-value)
                                     (drop 1)
                                     (remove nil?)
                                     first)]
              (when scenario-name
                (set! (.-value name-element) scenario-name))
              scenario-name)))]
    [:div.gherkin-editor
     [:div.scenario
      [:div {:class (when-not edit? "disabled")}
       [codemirror
        {:value           value
         :on-change       (fn [cm _]
                            (let [doc (.getDoc cm)
                                  tokens (mapcat (fn [line-number]
                                                   (map (fn [token]
                                                          {:string (.-string token)
                                                           :type   (.-type token)})
                                                        (.getLineTokens cm line-number true)))
                                                 (range (.lineCount doc)))
                                  variables (->> tokens
                                                 (filter #(= "variable" (:type %)))
                                                 (map (comp str/trim :string))
                                                 (filter #(re-matches #"^<.*>$" %))
                                                 (set))
                                  new-value (.getValue cm)
                                  scenario-name (set-name-element-value new-value)]
                              (rf/dispatch [::set-steps new-value])
                              (rf/dispatch [::set-variables variables])
                              (when on-change
                                (on-change new-value))
                              (when (and on-change2 scenario-name)
                                (on-change2 scenario-name))))
         :codemirror-opts {:readOnly    (if edit? false "nocursor")
                           :lineNumbers edit?}}]]]]))

(defn gherkin-editor [editable]
  (let [value                    @(rf/subscribe [::scenario-steps])
        test-name-element-id     @(rf/subscribe [::test-name-element-id])]
      [:div.gherkin-editor
       [:div.scenario
        [:div {:class (when-not editable "disabled")}
          [codemirror {:value value
                       :on-change (fn [cm _]
                                   (let [doc (.getDoc cm)
                                         tokens (mapcat (fn [line-number]
                                                          (map (fn [token]
                                                                 {:string (.-string token)
                                                                  :type (.-type token)})
                                                               (.getLineTokens cm line-number true)))
                                                        (range (.lineCount doc)))
                                         variables (->> tokens
                                                        (filter #(= "variable" (:type %)))
                                                        (map (comp str/trim :string))
                                                        (filter #(re-matches #"^<.*>$" %))
                                                        (set))]
                                     (rf/dispatch [::set-steps (.getValue cm)])
                                     (rf/dispatch [::set-variables variables])))
                      :codemirror-opts {:readOnly "nocursor"
                                        :lineNumbers false}
                      :test-name-element-id test-name-element-id}]]]]))

(defn gherkin-textarea-panel []
  (let [editable       @(rf/subscribe [::gherkin-mode])
        disabled_edit? @(rf/subscribe [::disabled-edit])]
    [:div.gherkin-container
     [:label
      [:span.pe-3 "Scenario"]
      [:span.ps-0.clickable.old-ui-icon
       {:class (when disabled_edit? "d-none")
        :on-click (fn [e]
                    (rf/dispatch [::toggle-mode])
                    (if editable
                      (do
                        (.setOption @cm-container "readOnly" "nocursor")
                        (.setOption @cm-container "lineNumbers" false))
                      (do
                        (.setOption @cm-container "readOnly" false)
                        (.setOption @cm-container "lineNumbers" true))))}
       (if editable [icons/svg-eye] [icons/pencil-fill])]]
     [gherkin-editor editable]]))

(defn ^:dev/after-load mount-root []
  (init/remount-roots "gherkin-textarea-component"
                      gherkin-textarea-panel))

(utils/register-webcomponent! "gherkin-textarea-component")
